import type { InternationalCommittee } from '../types/committee';

/**
 * Drop duplicate committee rows (same id or same code).
 * When preferredIds are provided, keep the matching row so a selected value stays valid.
 */
export function uniqueInternationalCommittees(
  committees: InternationalCommittee[] | null | undefined,
  preferredIds: Array<string | null | undefined> = []
): InternationalCommittee[] {
  if (!Array.isArray(committees) || committees.length === 0) return [];

  const preferred = new Set(
    preferredIds.map(id => id?.trim()).filter((id): id is string => !!id)
  );

  const byId = new Map<string, InternationalCommittee>();
  for (const committee of committees) {
    const id = committee?.id?.trim() || committee?.code?.trim();
    if (!id || byId.has(id)) continue;
    byId.set(id, committee);
  }

  const byCode = new Map<string, InternationalCommittee>();
  const uniqueById = Array.from(byId.values());
  for (const committee of uniqueById) {
    const code = committee.code?.trim().toLowerCase() || committee.id;
    const existing = byCode.get(code);
    if (!existing) {
      byCode.set(code, committee);
      continue;
    }
    if (preferred.has(committee.id) && !preferred.has(existing.id)) {
      byCode.set(code, committee);
    }
  }

  return Array.from(byCode.values());
}

/** Find a committee by stored value (GUID id or code such as AVICOM). */
export function findCommittee(
  committees: InternationalCommittee[],
  value?: string | null
): InternationalCommittee | undefined {
  if (!value) return undefined;
  const normalized = value.trim();
  return committees.find(
    c =>
      c.id === normalized ||
      c.code === normalized ||
      c.code.toLowerCase() === normalized.toLowerCase()
  );
}

/** Resolve any stored committee reference to its canonical code. */
export function toCommitteeCode(
  committees: InternationalCommittee[],
  value?: string | null
): string {
  if (!value) return '';
  return findCommittee(committees, value)?.code ?? value.trim();
}

export function normalizeCommitteeCodes(
  committees: InternationalCommittee[],
  values?: string[] | null
): string[] {
  if (!values?.length) return [];
  return values
    .map(v => toCommitteeCode(committees, v))
    .filter((code, index, arr) => code && arr.indexOf(code) === index);
}

export function getCommitteeLabel(
  committee: InternationalCommittee,
  isGreek: boolean
): string {
  const primary = isGreek ? committee.nameGr : committee.nameEn;
  const secondary = isGreek ? committee.nameEn : committee.nameGr;
  if (secondary && secondary !== primary) {
    return `${primary} (${committee.code})`;
  }
  return `${primary} (${committee.code})`;
}
